import { memo, useState, useCallback } from "react";
import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { getPaymentMethodsIsLoading, getPaymentMethodsData } from "../../model/selectors/paymentSelectors";
import { fetchPaymentMethods } from "../../model/services/fetchPaymentMethods";
import { CreditCard } from "../CreditCard/CreditCard";

import { deletePaymentMethod, makeDefaultPaymentMethod } from "@/shared/utils/api";
import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { handleError } from "@/shared/lib/helpers/utils";
import { Spinner } from "@/shared/ui/Spinner";
import { useAppDispatch } from "@/shared/lib/hooks/redux";

import cls from "./CreditCards.module.scss";

interface CreditCardsProps {
  className?: string;
}

export const CreditCards = memo(({ className }: CreditCardsProps) => {
  const dispatch = useAppDispatch();
  const { showAlert } = useAlert();

  const [changingCreditCards, setChangingCreditCards] = useState<boolean>(false);

  const isLoading = useSelector(getPaymentMethodsIsLoading);
  const data = useSelector(getPaymentMethodsData);

  const handleDelete = useCallback(
    async (paymentId: string) => {
      setChangingCreditCards(true);

      const { error } = await deletePaymentMethod(paymentId);

      if (error) {
        const err = handleError(error);

        return showAlert({
          theme: AlertTheme.ERROR,
          message: err.details,
        } as AlertOptions);
      }

      dispatch(fetchPaymentMethods());

      setChangingCreditCards(false);
    },
    [dispatch, showAlert]
  );

  const handleMakeDefault = useCallback(
    async (paymentId: string) => {
      setChangingCreditCards(true);

      const { error } = await makeDefaultPaymentMethod(paymentId);

      if (error) {
        const err = handleError(error);

        return showAlert({
          theme: AlertTheme.ERROR,
          message: err.details,
        } as AlertOptions);
      }

      dispatch(fetchPaymentMethods());

      setChangingCreditCards(false);
    },
    [dispatch, showAlert]
  );

  let content;

  if (isLoading) {
    content = (
      <div className={cls.Loading}>
        <Spinner />
      </div>
    );
  } else if (data) {
    content = (
      <>
        {data.cards.map((cardInfo) => (
          <CreditCard
            key={cardInfo.id}
            {...cardInfo}
            handleDelete={handleDelete}
            handleMakeDefault={handleMakeDefault}
            isDefault={cardInfo.id === data.defaultPaymentMethod}
            disableDelete={data.cards.length <= 1}
          />
        ))}
      </>
    );
  } else {
    content = <p>No payment methods found.</p>;
  }

  return (
    <div className={clsx(cls.CreditCards, {}, [className])}>
      {changingCreditCards && (
        <div className={cls.Loading}>
          <Spinner />
        </div>
      )}
      {content}
    </div>
  );
});
